
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NAV_LINKS } from '../../constants';
import { useAppContext } from '../../contexts/AppContext';
import { useTour } from '../../contexts/TourContext';
import { Shield, Download, ChevronDown, ChevronRight, LogOut } from 'lucide-react';

const PATH_TO_FEATURE: Record<string, string> = {
    // Core
    '/dashboard': 'dashboard',
    '/projects': 'projects',
    '/action-tracking': 'action_tracking',

    // Reliability Tools
    '/fmeas': 'fmea',
    '/p-diagram': 'p_diagram',
    '/risk-analysis': 'risk_matrix',
    '/control-plans': 'control_plan',

    // Validation
    '/reliability-plan': 'dvpr',
    '/dvpr': 'dvpr',
    '/lims': 'lims',
    '/physics-verification': 'physics',
    '/reliability-lab': 'physics',
    '/traceability-matrix': 'traceability',

    // AI Features
    '/ai-assistant': 'ai_assistant',
    '/magnus-assistant': 'ai_assistant',
    '/auto-fmea': 'auto_fmea',

    // Admin Features (usually not gated but included for completeness)
    '/admin/audit-logs': 'audit_logs',
    '/admin/api-access': 'api_access'
};

const Sidebar: React.FC = () => {
    const { viewMode, navigate, currentUser, logout, theme, hasFeature } = useAppContext();
    const { isTourActive, currentStep, tourSteps } = useTour();
    const location = useLocation();
    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

    // Auto-expand and scroll for tour
    React.useEffect(() => {
        if (!isTourActive) return;

        const step = tourSteps[currentStep];
        if (!step) return;

        // Extract path from selector [data-tour-id="/some-path"]
        const match = step.target.match(/data-tour-id="([^"]+)"/);
        const path = match ? match[1] : null;

        if (path) {
            // Check if this path belongs to a parent menu
            const parent = NAV_LINKS.find(p => p.children?.some(c => c.path === path));
            if (parent) {
                setExpandedMenus(prev => {
                    const newSet = new Set(prev);
                    if (!newSet.has(parent.name)) {
                        newSet.add(parent.name);
                        return newSet;
                    }
                    return prev;
                });
            }

            // Scroll into view after a short delay to allow expansion
            setTimeout(() => {
                const element = document.querySelector(step.target);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
        }
    }, [isTourActive, currentStep, tourSteps]);

    const handleNavClick = (e: React.MouseEvent, path: string, external?: boolean) => {
        e.preventDefault();
        if (external) {
            window.open(path, '_blank', 'noopener,noreferrer');
        } else {
            navigate(path);
        }
    };

    const toggleSubmenu = (e: React.MouseEvent, name: string) => {
        e.preventDefault();
        e.stopPropagation();
        const newSet = new Set(expandedMenus);
        if (newSet.has(name)) newSet.delete(name);
        else newSet.add(name);
        setExpandedMenus(newSet);
    };

    const hasAccess = (path: string, external?: boolean) => {
        // External links always accessible (they have their own auth)
        if (external) return true;

        if (!currentUser) return false;

        // 1. Capability/Subscription Gate
        const featureId = PATH_TO_FEATURE[path];
        if (featureId && !hasFeature(featureId)) {
            return false; // Hidden if plan doesn't include it
        }

        // 2. Super users have access to everything
        if (currentUser.role === 'admin' || currentUser.role === 'autoGainAdmin' || currentUser.role === 'magnus') return true;

        // 3. User Permission Gate
        return currentUser.permissions.includes(path) || currentUser.permissions.includes('*');
    };

    const visibleLinks = NAV_LINKS
        .filter(link => {
            if (link.advanced && viewMode !== 'advanced') return false;

            // Check legacy adminOnly flag
            if (link.adminOnly && currentUser?.role !== 'admin' && currentUser?.role !== 'autoGainAdmin') return false;

            // Handle requiredRole filtering for portal separation
            if (link.requiredRole) {
                const userRole = currentUser?.role;
                const isSuperAdmin = userRole === 'admin' || userRole === 'magnus';
                const isAutoGainStaff = userRole === 'autoGainAdmin';
                const isClientAdmin = userRole === 'clientAdmin';

                // AutoGain Portal: Only visible to AutoGain staff and super admins
                if (link.requiredRole === 'autoGainAdmin') {
                    if (!isAutoGainStaff && !isSuperAdmin) return false;
                }

                // Customer Portal: Visible to clientAdmin, AutoGain staff, and super admins
                if (link.requiredRole === 'clientAdmin') {
                    if (!isClientAdmin && !isAutoGainStaff && !isSuperAdmin) return false;
                }
            }

            if (link.children) {
                return link.children.some(child => hasAccess(child.path, child.external));
            }

            return hasAccess(link.path);
        })
        .map(link => {
            if (link.children) {
                return {
                    ...link,
                    children: link.children.filter(child => hasAccess(child.path, child.external))
                };
            }
            return link;
        });

    if (hasAccess('/export') && viewMode === 'advanced') {
        if (!visibleLinks.some(l => l.path === '/export')) {
            visibleLinks.push({ name: 'Export', path: '/export', icon: <Download size={20} /> });
        }
    }

    return (
        <aside className={`w-64 flex-shrink-0 flex flex-col pt-0 pb-4 shadow-sm overflow-y-auto border-r relative bg-[var(--bg-secondary)] border-[var(--border-color)]`}>
            {/* Logo Header with Glow Effect */}
            <div className="h-20 flex items-center justify-between px-4 border-b border-[var(--border-color)] mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-blue-500/50 transition-all duration-300">
                        <Shield className="h-7 w-7 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                            Magnus-DFR-Enhanced
                        </h2>
                        <p className="text-xs text-slate-500 dark:text-gray-400">Design for Reliability</p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1">
                <ul className="space-y-0.5">
                    {visibleLinks.map(link => {
                        const hasChildren = link.children && link.children.length > 0;
                        const isExpanded = expandedMenus.has(link.name);
                        const isActive = location.pathname === link.path || (hasChildren && link.children?.some(child => location.pathname === child.path));

                        return (
                            <li key={link.name} className="relative">
                                {/* Parent Menu Item */}
                                <div
                                    data-tour-id={hasChildren ? `nav-root-${link.path}` : link.path}
                                    onClick={(e) => hasChildren ? toggleSubmenu(e, link.name) : handleNavClick(e, link.path, link.external)}
                                    className={`
                                        group relative flex items-center justify-between gap-3 px-3 py-2.5 my-0.5 rounded-xl
                                        transition-all duration-200 cursor-pointer
                                        ${isActive
                                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-700 dark:text-blue-300 shadow-sm font-medium scale-[1.01]'
                                            : 'hover:bg-slate-100 dark:hover:bg-gray-700/40 hover:translate-x-1'
                                        }
                                        ${hasChildren && isExpanded ? 'bg-white/50 dark:bg-gray-700/30' : ''}
                                    `}
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className={`
                                            p-1.5 w-6 h-6 flex items-center justify-center rounded-lg transition-all duration-200
                                            ${isActive
                                                ? 'bg-blue-500 shadow-md text-white'
                                                : 'bg-slate-200 dark:bg-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 text-slate-600 dark:text-gray-300'
                                            }
                                        `}>
                                            {React.cloneElement(link.icon as any, {
                                                className: `transition-colors duration-200 ${isActive
                                                    ? 'text-white'
                                                    : 'text-slate-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                                                    }`,
                                                size: 14
                                            })}
                                        </div>
                                        <span className={`
                                            font-medium text-sm transition-colors duration-200
                                            ${isActive
                                                ? 'text-blue-700 dark:text-blue-300 font-medium'
                                                : 'text-slate-600 dark:text-gray-300'
                                            }
                                        `}>
                                            {link.name}
                                        </span>
                                    </div>

                                    {/* Expand/Collapse Icon */}
                                    {hasChildren && (
                                        <div className={`
                                            transition-all duration-300 p-1 rounded-lg
                                            ${isExpanded ? 'rotate-180 bg-blue-100 dark:bg-blue-900/40' : 'rotate-0 group-hover:bg-slate-100 dark:group-hover:bg-gray-600'}
                                        `}>
                                            <ChevronDown size={14} className={`
                                                ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-gray-400'}
                                            `} />
                                        </div>
                                    )}

                                    {/* Active Indicator */}
                                    {isActive && !hasChildren && (
                                        <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                    )}
                                </div>

                                {/* Submenu with Smooth Transition */}
                                {hasChildren && (
                                    <div className={`
                                        overflow-hidden transition-all duration-300 ease-in-out
                                        ${isExpanded ? 'max-h-[32rem] opacity-100 mt-1' : 'max-h-0 opacity-0'}
                                    `}>
                                        <ul className="ml-4 pl-4 border-l-2 border-blue-200 dark:border-blue-800/50 space-y-0.5">
                                            {link.children?.map(child => {
                                                // Handle divider items as section headers
                                                if (child.divider) {
                                                    return (
                                                        <li key={child.path} className="pt-3 pb-1">
                                                            <span className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider">
                                                                {child.name.replace(/─/g, '').trim()}
                                                            </span>
                                                        </li>
                                                    );
                                                }
                                                const isChildActive = location.pathname === child.path;
                                                return (
                                                    <li key={child.path}>
                                                        <div
                                                            data-tour-id={child.path}
                                                            onClick={(e) => handleNavClick(e, child.path, child.external)}
                                                            className={`
                                                                group relative flex items-center gap-2 px-3 py-2 rounded-lg
                                                                transition-all duration-200 cursor-pointer
                                                                ${isChildActive
                                                                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-700 dark:text-blue-300 shadow-sm font-medium scale-[1.01]'
                                                                    : 'hover:bg-slate-100 dark:hover:bg-gray-700/40 hover:translate-x-1'
                                                                }
                                                            `}
                                                        >
                                                            {/* Child Icon */}
                                                            <div className={`
                                                                w-6 h-6 rounded-md flex items-center justify-center transition-all
                                                                ${isChildActive
                                                                    ? 'bg-blue-500 shadow-md'
                                                                    : 'bg-slate-200 dark:bg-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40'
                                                                }
                                                            `}>
                                                                {child.icon ? (
                                                                    React.cloneElement(child.icon as any, {
                                                                        size: 14,
                                                                        className: isChildActive ? 'text-white' : 'text-slate-600 dark:text-gray-300'
                                                                    })
                                                                ) : (
                                                                    <ChevronRight size={12} className={isChildActive ? 'text-white' : 'text-slate-500 dark:text-gray-400'} />
                                                                )}
                                                            </div>

                                                            {/* Child Name */}
                                                            <span className={`
                                                                text-sm transition-colors
                                                                ${isChildActive
                                                                    ? 'text-blue-700 dark:text-blue-300 font-medium'
                                                                    : 'text-slate-600 dark:text-gray-300 group-hover:text-slate-900 dark:group-hover:text-white'
                                                                }
                                                            `}>
                                                                {child.name}
                                                            </span>

                                                            {/* Active Dot */}
                                                            {isChildActive && (
                                                                <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Footer Info */}
            <div className="mt-auto pt-4 border-t border-slate-200 dark:border-gray-700 space-y-2">
                <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                    <p className="text-xs font-medium text-slate-700 dark:text-gray-300">
                        {currentUser?.username || 'Guest User'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-gray-400 capitalize">
                        {currentUser?.role || 'View'} Mode
                    </p>
                </div>
                {currentUser && (
                    <button
                        onClick={logout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                    >
                        <LogOut size={14} />
                        Log Out
                    </button>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
